import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, FormControlLabel } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classes from './ExpenseDetailStep.module.scss';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';
import { generalEnums } from '../../../../../utils/enums';
import CommonFileUploader from '../../../../fileupload/CommonFileUploader/CommonFileUploader';
import { AttachmentItem } from '../../../timesheet/Attachments/Attachments';
import CustomDayPicker from '../../../../common/CustomDayPicker/CustomDayPicker';
import ExpenseDetailMobile from '../../../expense/components/ExpenseDetailMobile/ExpenseDetailMobile';

const autocompleteStyles = {
  '& .MuiSvgIcon-root': {
    color: '#D1D3D4',
    fontSize: '24px'
  }
};

const textFieldStyles = (width) => ({
  width,
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #D1D3D4',
    borderRadius: '10px'
  },
  color: '#283237',
  fontSize: '16px',
  fontWeight: '325',
  lineHeight: '19px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
});

export default function ExpenseDetailStep({
  getStates,
  expenseCurrency,
  category,
  categories,
  setCategory,
  date,
  setDate,
  description,
  setDescription,
  attachmentHandler,
  setNoReceiptCheck,
  error
}) {
  const { t } = useTranslation();
  const size = useWindowSize();
  const [anchorEl, setAnchorEl] = useState(null);
  const [checked, setChecked] = useState(false);
  const [files, setFiles] = useState([]);

  const handleCalendar = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const handlePicker = (dt) => {
    setDate(dt);
    setAnchorEl(null);
  };

  const handleChecked = (e) => {
    setChecked(e.target.checked);
    setNoReceiptCheck(e.target.checked);
    if (e.target.checked) {
      setFiles([]);
    }
  };

  const uploadHandler = (file) => {
    setFiles(file);
    attachmentHandler({ file, isChecked: checked });
  };

  const deleteHandler = (name) => {
    const filteredFiles = [...files].filter((file) => file.name !== name);
    setFiles(filteredFiles);
    attachmentHandler({ file: filteredFiles, isChecked: checked });
  };

  return (
    <div className={classes.container}>
      {size.width > responsiveSizeTimesheet ? (
        <>
          <p className={classes.text}>{t('dashboard.createclaim.steps.createanexpense')}</p>
          <div className={classes.filedsContainer}>
            <Autocomplete
              disableClearable
              value={category.label}
              options={categories}
              sx={autocompleteStyles}
              popupIcon={<ExpandMoreIcon />}
              forcePopupIcon
              onChange={(event, newValue) => {
                setCategory(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`${t('dashboard.createclaim.steps.category')}*`}
                  sx={textFieldStyles(size.width > responsiveSizeTimesheet ? '242px' : '300px')}
                />
              )}
            />
            <CustomDayPicker
              date={date}
              dayFormat={generalEnums.DATEFIRSTFORMAT}
              changeHandler={(newValue) => handlePicker(newValue)}
              sx={textFieldStyles(size.width > responsiveSizeTimesheet ? '242px' : '300px')}
            />
          </div>
          <div className={classes.filedsContainer}>
            <TextField
              placeholder={t('dashboard.createclaim.steps.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={textFieldStyles('100%')}
            />
          </div>
          <div className={classes.filedsContainer}>
            <CommonFileUploader
              uploadHandler={uploadHandler}
              isMultiple
              acceptedFormats={['.jpeg', '.jpg', '.png', '.PNG', '.pdf', '.doc', '.docx', '.csv', '.xls', 'xlsx']}
              maxFileSizeMB={5}
              allowedQtyOfAttachments={10}
              existingAttachmentsQty={[]}
              disabled={checked}
            />
          </div>
          <div className={classes.attachmentsList}>
            {[...files].map((file) => (
              <AttachmentItem key={file.name} label={`${file.name}`} deleteHandler={() => deleteHandler(file.name)} />
            ))}
          </div>
          <div className={classes.checkBoxContainer}>
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={handleChecked} />}
              label={t('dashboard.createclaim.steps.noreceipt')}
              sx={{ margin: 0, 'MuiFormControlLabel-label': { fontSize: 9 } }}
            />
          </div>
          {size.width > responsiveSizeTimesheet && error && <p className={classes.error}>{error}</p>}
          {size.width > responsiveSizeTimesheet && !error && <p className={classes.error}>&nbsp;</p>}
        </>
      ) : (
        <ExpenseDetailMobile expenseCurrency={expenseCurrency} getStateData={getStates} />
      )}
    </div>
  );
}
